import { Suspense, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { Loader } from '@givebutter/primitives/loader';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faChevronDown, faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContacts } from 'api/contacts/contacts';
import { NewContactId } from 'assets/js/pages/contacts/ContactForm';
import { ContactOrCompanyLabel } from 'assets/js/pages/contacts/ContactOrCompanyLabel';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import { SoftCreditFormValues } from 'assets/js/pages/transactions/soft-credits/types';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteContacts } from 'customHooks/useInfiniteContacts';
import { useIsPending } from 'customHooks/useIsPending';
import { first, uniqBy } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';
import { PaginatedData } from 'types/pagination';

const SoftCreditFormFields = ({ required = false }: { required?: boolean }) => {
    const { path, watch } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const [rawSelectedContact, contactType] = watch([
        'softCredit.selectedContact',
        'softCredit.contactType',
    ]);
    const selectedContact = rawSelectedContact?.[0];
    const isEphemeralContact = selectedContact === NewContactId;
    const setDefaultCommunicationFields = useSetDefaultCommunicationFields();

    return (
        <div className="relative flex flex-col gap-4">
            <Suspense fallback={<Loader />}>
                <Form.Container>
                    <Form.Label name={path('softCredit.selectedContact')} className="sr-only">
                        Contact
                        <Form.LabelAsterisk />
                    </Form.Label>
                    <SoftCreditContactSelectField required={required} />
                    <Form.Error name={path('softCredit.selectedContact')} />
                </Form.Container>
                {isEphemeralContact && (
                    <div className="flex flex-col gap-4">
                        <CompanyForm.ContactTypeToggle
                            name="softCredit.contactType"
                            onChange={value => setDefaultCommunicationFields(value as ContactType)}
                        />

                        {contactType === ContactType.Company ? (
                            <SoftCreditCompanyNameField />
                        ) : (
                            <SoftCreditNameField />
                        )}

                        <SoftCreditEmailField />
                        <SoftCreditPhoneField />
                    </div>
                )}
                <SoftCreditAmountField />
                <SoftCreditNoteField />
            </Suspense>
        </div>
    );
};

const SoftCreditContactSelectField = ({ required = false }: { required?: boolean }) => {
    const { accountId } = useCurrentAccount();
    const { control, watch, setValue } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const resetContact = useResetContact();
    const [searchContacts, setSearchContacts] = useState('');
    const debouncedSearchContacts = useDebounce(searchContacts);
    const selectedContact = watch('softCredit.selectedContact');

    const { data: contactsData, ref: contactsRef } = useInfiniteContacts({
        query: debouncedSearchContacts,
        nameRequired: true,
    });

    const [_, params] = useIsPending({
        ids: selectedContact.map(Number),
        page: 1,
        per_page: selectedContact.length,
    });

    const { data: savedContacts } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactsWithFilters(accountId, params),
        queryFn: () => {
            if (selectedContact.length === 0) {
                return Promise.resolve({
                    data: [],
                    links: {} as any,
                    meta: {} as any,
                } as PaginatedData<Contact>);
            }

            return fetchContacts(accountId, params);
        },
    });

    const fetchedContacts = contactsData?.pages ?? [];
    const displayedContacts = uniqBy([...savedContacts.data, ...fetchedContacts], 'id');

    return (
        <Form.Combobox
            control={control}
            name="softCredit.selectedContact"
            allowMultiple={false}
            rules={{ required: required && 'Contact is required' }}
            onChangeValue={value => {
                if (first(value) === NewContactId) {
                    resetContact();
                }
            }}
        >
            <Combobox.Trigger aria-label="Select existing contact or create new">
                <Combobox.Select>
                    <Combobox.SelectValue placeholder="Select existing contact or create new" />
                    <div className="flex items-center gap-2">
                        {selectedContact.length > 0 ? (
                            <Button
                                variant="none"
                                onClick={() => setValue('softCredit.selectedContact', [])}
                                aria-label="clear selection"
                            >
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="size-3.5 text-gray-500"
                                />
                            </Button>
                        ) : (
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="size-3.5 text-gray-500"
                            />
                        )}
                    </div>
                </Combobox.Select>
            </Combobox.Trigger>
            <Combobox.Popover>
                <Combobox.Search
                    value={searchContacts}
                    onChangeValue={setSearchContacts}
                    placeholder="Search contacts"
                />
                <Combobox.Items>
                    <Combobox.Item key={NewContactId} value={NewContactId}>
                        <FontAwesomeIcon icon={faPlus} />
                        <Combobox.ItemText>
                            <p className="font-medium">Create new contact</p>
                        </Combobox.ItemText>
                    </Combobox.Item>
                    {displayedContacts.map(contact => (
                        <Combobox.Item value={contact.id.toString()} key={contact.id}>
                            <Combobox.ItemText>
                                <div className="flex flex-col text-sm font-medium">
                                    <ContactOrCompanyLabel contact={contact} />
                                    {contact.primary_email && (
                                        <p className="font-thin text-gray-600">
                                            {contact.primary_email}
                                        </p>
                                    )}
                                </div>
                            </Combobox.ItemText>
                            <Combobox.CheckmarkIndicator />
                        </Combobox.Item>
                    ))}
                    <div ref={contactsRef} />
                </Combobox.Items>
            </Combobox.Popover>
        </Form.Combobox>
    );
};

const SoftCreditNameField = () => {
    const { control, path, watch } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const contactType = watch('softCredit.contactType');

    return (
        <div className="flex flex-row gap-4">
            <Form.Container>
                <Form.Label name={path('softCredit.firstName')}>
                    First name
                    <Form.LabelAsterisk />
                </Form.Label>
                <div className="flex gap-2">
                    <Form.TextInputRoot
                        control={control}
                        name="softCredit.firstName"
                        rules={{
                            required:
                                contactType === ContactType.Individual && 'First name is required',
                        }}
                    >
                        <Form.TextInputForm placeholder="First name" />
                    </Form.TextInputRoot>
                </div>
                <Form.Error name={path('softCredit.firstName')} />
            </Form.Container>

            <Form.Container>
                <Form.Label name={path('softCredit.lastName')}>
                    Last name
                    <Form.LabelAsterisk />
                </Form.Label>
                <div className="flex gap-2">
                    <Form.TextInputRoot
                        control={control}
                        name="softCredit.lastName"
                        rules={{
                            required:
                                contactType === ContactType.Individual && 'Last name is required',
                        }}
                    >
                        <Form.TextInputForm placeholder="Last name" />
                    </Form.TextInputRoot>
                </div>
                <Form.Error name={path('softCredit.lastName')} />
            </Form.Container>
        </div>
    );
};

const SoftCreditCompanyNameField = () => {
    const { control, path, watch } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const contactType = watch('softCredit.contactType');

    return (
        <Form.Container>
            <Form.Label name={path('softCredit.companyName')}>
                Company name
                <Form.LabelAsterisk />
            </Form.Label>
            <div className="flex gap-2">
                <Form.TextInputRoot
                    control={control}
                    name="softCredit.companyName"
                    rules={{
                        required: contactType === ContactType.Company && 'Company name is required',
                    }}
                >
                    <Form.TextInputForm placeholder="Company name" />
                </Form.TextInputRoot>
            </div>
            <Form.Error name={path('softCredit.companyName')} />
        </Form.Container>
    );
};

const SoftCreditEmailField = () => {
    const { control, path } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const emailFieldArray = useFieldArray({
        name: 'softCredit.emails',
        control,
    });
    const { fields: emailFields } = emailFieldArray;
    return emailFields.map((field, index) => (
        <div key={field.id} className="flex flex-col gap-3">
            <Form.Container>
                <Form.Label name={path(`softCredit.emails.${index}.value`)}>Email</Form.Label>
                <div className="flex w-full items-center gap-3">
                    <Form.TextInputRoot control={control} name={`softCredit.emails.${index}.value`}>
                        <Form.TextInputForm placeholder="Email" />
                    </Form.TextInputRoot>
                    <Form.SingleSelect control={control} name={`softCredit.emails.${index}.type`}>
                        <SingleSelect.Trigger
                            aria-label="Email type"
                            className="basis-min-content"
                        />
                        <SingleSelect.Content>
                            <SingleSelect.Item value="personal">
                                <SingleSelect.ItemText>Personal</SingleSelect.ItemText>
                            </SingleSelect.Item>
                            <SingleSelect.Item value="work">
                                <SingleSelect.ItemText>Work</SingleSelect.ItemText>
                            </SingleSelect.Item>
                        </SingleSelect.Content>
                    </Form.SingleSelect>
                </div>
                <Form.UseFormError name={path(`softCredit.emails.${index}.value`)} />
            </Form.Container>
        </div>
    ));
};

const SoftCreditPhoneField = () => {
    const { control, path } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const phoneFieldArray = useFieldArray({
        name: 'softCredit.phones',
        control,
    });
    const { fields: phoneFields } = phoneFieldArray;

    return (
        <>
            {phoneFields.map((field, index) => (
                <div key={field.id} className="flex flex-col gap-3">
                    <Form.Container>
                        <Form.Label name={path(`softCredit.phones.${index}.value`)}>
                            Phone number
                        </Form.Label>
                        <div className="flex w-full items-center gap-3">
                            <Form.TextInputRoot
                                control={control}
                                name={`softCredit.phones.${index}.value`}
                            >
                                <Form.TextInputForm placeholder="+1 (000) 000 - 0000" />
                            </Form.TextInputRoot>
                            <Form.SingleSelect
                                control={control}
                                name={`softCredit.phones.${index}.type`}
                            >
                                <SingleSelect.Trigger
                                    aria-label="Phone type"
                                    className="basis-min-content"
                                />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="home">
                                        <SingleSelect.ItemText>Home</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="work">
                                        <SingleSelect.ItemText>Work</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="cell">
                                        <SingleSelect.ItemText>Cell</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </div>
                        <Form.UseFormError name={path(`softCredit.phones.${index}.value`)} />
                    </Form.Container>
                </div>
            ))}
        </>
    );
};

const SoftCreditAmountField = () => {
    const { control, path } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    return (
        <Form.Container>
            <Form.Label name={path('softCredit.amount')}>Amount credited</Form.Label>
            <Form.TextInputRoot
                control={control}
                name="softCredit.amount"
                inputRootProps={{ mask: 'decimal' }}
            >
                <InputCurrencyIcon />
                <Form.TextInputForm />
            </Form.TextInputRoot>
            <Form.Error name={path('softCredit.amount')} />
        </Form.Container>
    );
};

const SoftCreditNoteField = () => {
    const { control, path, watch } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    const [note] = watch(['softCredit.note']);
    const [fieldVisible, setFieldVisible] = useState(!!note);

    return (
        <Form.Container>
            {fieldVisible ? (
                <>
                    <Form.Label name={path('softCredit.note')}>Note</Form.Label>
                    <Form.Textarea control={control} name="softCredit.note" />
                    <Form.Error name={path('softCredit.note')} />
                </>
            ) : (
                <Button
                    variant="none"
                    color="primary"
                    className="font-medium"
                    onClick={() => setFieldVisible(true)}
                >
                    Add a note +
                </Button>
            )}
        </Form.Container>
    );
};

const useResetContact = () => {
    const { resetField } = Form.useGivebutterFormContext<SoftCreditFormValues>();
    return () => {
        resetField('softCredit.firstName');
        resetField('softCredit.lastName');
        resetField('softCredit.companyName');
        resetField('softCredit.contactType');
        resetField('softCredit.emails');
        resetField('softCredit.phones');
    };
};

const useSetDefaultCommunicationFields = () => {
    const { resetField } = Form.useGivebutterFormContext<SoftCreditFormValues>();

    return (contactType: ContactType) => {
        resetField('softCredit.emails', {
            defaultValue: [
                {
                    value: '',
                    is_primary: true,
                    type: contactType === ContactType.Company ? 'work' : 'personal',
                },
            ],
        });
        resetField('softCredit.phones', {
            defaultValue: [
                {
                    value: '',
                    is_primary: true,
                    type: contactType === ContactType.Company ? 'work' : 'home',
                },
            ],
        });
    };
};

export { SoftCreditFormFields };
